import { formatPaymentButtonLabel } from './markdownTipTapBlocks';

/**
 * Label for a paid payment button chip.
 * @param {number|null} amountCents
 * @param {string} [existingLabel]
 * @returns {string}
 */
export function paidPaymentButtonLabel(amountCents, existingLabel = '') {
  if (amountCents != null && amountCents >= 50) {
    return formatPaymentButtonLabel(amountCents).replace(/^Pay\s*/i, 'Paid ');
  }
  const trimmed = String(existingLabel || '').trim();
  if (trimmed) return trimmed.replace(/^Pay\s*/i, 'Paid ');
  return 'Paid';
}

const PAID_CHIP_STYLE = {
  background: 'rgba(34, 197, 94, 0.15)',
  border: '1px solid rgba(34, 197, 94, 0.4)',
  color: 'rgb(22, 163, 74)',
  fontWeight: '500',
  cursor: 'default',
};

const PAY_CHIP_STYLE = {
  background: 'rgba(59, 130, 246, 0.15)',
  border: '1px solid rgba(59, 130, 246, 0.4)',
  color: 'rgb(37, 99, 235)',
  fontWeight: '500',
  cursor: 'pointer',
};

/**
 * Apply paid or unpaid styling to [data-payment-button] nodes in a container.
 * Supports multiple independent payment links: when `paidLinkIds` is provided,
 * only buttons whose data-payment-link-id is in the set are marked paid. Passing
 * `paymentComplete: true` marks every button paid (legacy / all-paid case).
 *
 * @param {HTMLElement|null} container
 * @param {{ paymentComplete?: boolean, paidLinkIds?: Set<string>|string[]|null, paymentAmountCents?: number|null, onPaymentClick?: ((info: {paymentLinkId: string|null, amountCents: number|null}) => void)|null }} options
 */
export function applyPaymentButtonReadOnlyState(container, {
  paymentComplete = false,
  paidLinkIds = null,
  paymentAmountCents = null,
  onPaymentClick = null,
}) {
  if (!container) return () => {};

  const paidSet = paidLinkIds instanceof Set
    ? paidLinkIds
    : (Array.isArray(paidLinkIds) ? new Set(paidLinkIds) : null);

  const handlers = [];

  container.querySelectorAll('[data-payment-button]').forEach((el) => {
    const linkId = el.getAttribute('data-payment-link-id') || null;
    const elAmountAttr = el.getAttribute('data-payment-amount-cents');
    const elAmountCents = elAmountAttr != null && elAmountAttr !== '' ? Number(elAmountAttr) : null;
    const isPaid = !!paymentComplete || (paidSet && linkId && paidSet.has(linkId));

    if (isPaid) {
      const existing = el.getAttribute('data-payment-label') || el.textContent || '';
      const label = paidPaymentButtonLabel(elAmountCents ?? paymentAmountCents, existing);
      el.textContent = label;
      el.setAttribute('data-payment-label', label);
      el.setAttribute('data-payment-paid', 'true');
      Object.assign(el.style, {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 8px',
        borderRadius: '4px',
        lineHeight: '1',
        verticalAlign: 'middle',
        ...PAID_CHIP_STYLE,
      });
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
      return;
    }

    el.removeAttribute('data-payment-paid');
    Object.assign(el.style, {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2px 8px',
      borderRadius: '4px',
      lineHeight: '1',
      verticalAlign: 'middle',
      ...PAY_CHIP_STYLE,
    });
    el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');

    if (typeof onPaymentClick === 'function') {
      if (el.dataset.paymentClickWired === 'true') return;
      el.dataset.paymentClickWired = 'true';
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const amountAttr = el.getAttribute('data-payment-amount-cents');
        const amountCents = amountAttr != null && amountAttr !== '' ? Number(amountAttr) : null;
        onPaymentClick({ paymentLinkId: linkId, amountCents });
      };
      el.addEventListener('click', handler);
      handlers.push({ el, handler });
    }
  });

  return () => {
    handlers.forEach(({ el, handler }) => {
      el.removeEventListener('click', handler);
      delete el.dataset.paymentClickWired;
    });
  };
}

/**
 * Replace payment buttons in a DOM subtree for PDF export. Supports multiple
 * independent links: paid links render green; unpaid links become a pay link
 * carrying their own paymentLinkId.
 * @param {ParentNode} root
 * @param {{ paymentComplete?: boolean, paidLinkIds?: Set<string>|string[]|null, paymentAmountCents?: number|null, paymentButtonUrl?: string|null }} options
 */
export function applyPaymentButtonsForPdf(root, {
  paymentComplete = false,
  paidLinkIds = null,
  paymentAmountCents = null,
  paymentButtonUrl = null,
}) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  const paidSet = paidLinkIds instanceof Set
    ? paidLinkIds
    : (Array.isArray(paidLinkIds) ? new Set(paidLinkIds) : null);

  root.querySelectorAll('[data-payment-button]').forEach((el) => {
    const linkId = el.getAttribute('data-payment-link-id') || null;
    const elAmountAttr = el.getAttribute('data-payment-amount-cents');
    const elAmountCents = elAmountAttr != null && elAmountAttr !== '' ? Number(elAmountAttr) : null;
    const isPaid = !!paymentComplete || (paidSet && linkId && paidSet.has(linkId));

    if (isPaid) {
      const existing = el.getAttribute('data-payment-label') || el.textContent || '';
      const label = paidPaymentButtonLabel(elAmountCents ?? paymentAmountCents, existing);
      el.textContent = label;
      el.style.color = 'rgb(22, 163, 74)';
      el.style.fontWeight = '600';
      el.style.textDecoration = 'none';
      return;
    }

    if (paymentButtonUrl && typeof paymentButtonUrl === 'string') {
      const label = el.getAttribute('data-payment-label') || el.textContent || 'Click to Pay';
      const href = linkId
        ? `${paymentButtonUrl}${paymentButtonUrl.includes('?') ? '&' : '?'}paymentLinkId=${encodeURIComponent(linkId)}`
        : paymentButtonUrl;
      const a = document.createElement('a');
      a.href = href;
      a.textContent = label;
      a.style.color = 'rgb(37, 99, 235)';
      a.style.textDecoration = 'underline';
      el.innerHTML = '';
      el.appendChild(a);
    }
  });
}

/**
 * @param {string|null} isoString
 * @returns {string}
 */
export function formatPaymentCompletedDisplay(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return isoString;
  }
}
